import React, { useState, useEffect } from 'react';
import { adminGetMembers, adminUpdateMember, adminDeleteMember, adminUploadMemberPicture, adminDeleteMemberPicture, adminResetMemberPassword } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { toast } from 'sonner';
import { Search, Edit, Trash2, Check, X, Plus, UserPlus, Copy, Camera, KeyRound } from 'lucide-react';
import api from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editingDates, setEditingDates] = useState(false);
  const [memberStart, setMemberStart] = useState('');
  const [memberEnd, setMemberEnd] = useState('');
  const [savingDates, setSavingDates] = useState(false);
  const [resetPwDialogOpen, setResetPwDialogOpen] = useState(false);
  const [resetPwUserId, setResetPwUserId] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const [resettingPw, setResettingPw] = useState(false);

  // Create form
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    birth_date: '',
    membership_type: 'yearly',
    is_active: true,
    payment_confirmed: true,
  });

  const resetCreateForm = () => {
    setCreateForm({
      name: '', email: '', password: '', phone: '', address: '',
      birth_date: '', membership_type: 'yearly', is_active: true, payment_confirmed: true,
    });
    setCreatedResult(null);
  };

  const fetchMembers = async () => {
    try {
      const response = await adminGetMembers();
      setMembers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Mitglieder');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleToggleActive = async (member) => {
    try {
      await adminUpdateMember(member.user_id, { is_active: !member.is_active });
      toast.success(`Mitglied ${member.is_active ? 'deaktiviert' : 'aktiviert'}`);
      fetchMembers();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Mitglied "${member.name}" wirklich löschen?`)) return;
    try {
      await adminDeleteMember(member.user_id);
      toast.success('Mitglied gelöscht');
      fetchMembers();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleResetPassword = async () => {
    if (resetPwValue.length < 6) { toast.error('Mindestens 6 Zeichen'); return; }
    setResettingPw(true);
    try {
      await adminResetMemberPassword(resetPwUserId, resetPwValue);
      toast.success('Passwort zurückgesetzt');
      setResetPwDialogOpen(false);
      setResetPwValue('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setResettingPw(false); }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email) {
      toast.error('Name und E-Mail sind Pflichtfelder');
      return;
    }
    setCreating(true);
    try {
      const response = await api.post('/admin/members', createForm);
      setCreatedResult(response.data);
      toast.success(`Mitglied ${response.data.name} erstellt! Mitgliedsnr: ${response.data.member_number}`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  const handlePhotoUpload = async (userId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploadingPhoto(userId);
      try {
        const fd = new FormData();
        fd.append('file', file);
        await adminUploadMemberPicture(userId, fd);
        toast.success('Profilbild hochgeladen');
        fetchMembers();
        // Update selected member if open
        if (selectedMember?.user_id === userId) {
          const res = await api.get(`/admin/members/${userId}`);
          setSelectedMember(res.data);
        }
      } catch (err) {
        toast.error('Upload fehlgeschlagen');
      } finally {
        setUploadingPhoto(null);
      }
    };
    input.click();
  };

  const handleDeletePhoto = async (userId) => {
    try {
      await adminDeleteMemberPicture(userId);
      toast.success('Profilbild entfernt');
      fetchMembers();
      if (selectedMember?.user_id === userId) {
        setSelectedMember({ ...selectedMember, profile_picture: null });
      }
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  const handleSaveName = async () => {
    if (!newName.trim() || !selectedMember) return;
    setSavingName(true);
    try {
      await adminUpdateMember(selectedMember.user_id, { name: newName.trim() });
      toast.success('Name aktualisiert');
      setEditingName(false);
      setSelectedMember({ ...selectedMember, name: newName.trim() });
      fetchMembers();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveDates = async () => {
    if (!selectedMember) return;
    setSavingDates(true);
    try {
      const updates = {};
      if (memberStart) updates.membership_start = new Date(memberStart).toISOString();
      if (memberEnd) updates.membership_end = new Date(memberEnd).toISOString();
      await adminUpdateMember(selectedMember.user_id, updates);
      toast.success('Mitgliedsdatum aktualisiert');
      setEditingDates(false);
      setSelectedMember({ ...selectedMember, ...updates });
      fetchMembers();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingDates(false);
    }
  };

  const filteredMembers = members.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.member_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-members-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Mitglieder</h1>
        <div className="flex items-center gap-3">
          <Badge variant="outline">{members.length} Gesamt</Badge>
          <Button
            onClick={() => { resetCreateForm(); setCreateDialogOpen(true); }}
            className="btn-primary"
            data-testid="create-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Mitglied anlegen
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
        <Input
          placeholder="Suchen nach Name, E-Mail oder Mitgliedsnummer..."
          className="input-dark pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="member-search-input"
        />
      </div>

      {/* Table */}
      <Card className="card-surface overflow-hidden">
        {loading ? (
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-white/10 rounded" />)}
            </div>
          </CardContent>
        ) : filteredMembers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead>Mitgliedsnr.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="hidden md:table-cell">E-Mail</TableHead>
                <TableHead className="hidden lg:table-cell">Registriert</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.user_id} className="border-white/10">
                  <TableCell className="font-mono text-accent text-xs">
                    {member.member_number || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={getPhotoUrl(member.profile_picture)} />
                        <AvatarFallback className="bg-surface-elevated text-xs">{member.name?.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{member.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-text-secondary text-sm">
                    {member.email}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-text-secondary text-sm">
                    {member.created_at ? formatDate(member.created_at) : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge className={member.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                      {member.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleToggleActive(member)}
                        title={member.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        data-testid={`toggle-member-${member.user_id}`}>
                        {member.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => { setSelectedMember(member); setEditingName(false); setEditingDates(false); setEditDialogOpen(true); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                        onClick={() => handleDelete(member)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <CardContent className="p-8 text-center">
            <p className="text-text-secondary">Keine Mitglieder gefunden.</p>
          </CardContent>
        )}
      </Card>

      {/* View/Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Mitglied Details</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              {/* Profile Photo */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={getPhotoUrl(selectedMember.profile_picture)} />
                    <AvatarFallback className="bg-surface-elevated text-xl font-heading">
                      {selectedMember.name?.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    onClick={() => handlePhotoUpload(selectedMember.user_id)}
                    className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center"
                    data-testid="upload-member-photo-btn"
                  >
                    {uploadingPhoto === selectedMember.user_id ? (
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-3.5 h-3.5 text-white" />
                    )}
                  </button>
                </div>
                <div>
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <Input
                        className="input-dark h-8 text-sm"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                        autoFocus
                        data-testid="edit-member-name-input"
                      />
                      <Button size="sm" className="btn-primary h-8 px-2" onClick={handleSaveName} disabled={savingName}>
                        <Check className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingName(false)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="font-heading font-bold text-lg">{selectedMember.name}</p>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setNewName(selectedMember.name || ''); setEditingName(true); }} data-testid="edit-member-name-btn">
                        <Edit className="w-3.5 h-3.5 text-text-secondary" />
                      </Button>
                    </div>
                  )}
                  <p className="text-text-secondary text-sm">{selectedMember.email}</p>
                  {selectedMember.profile_picture && (
                    <Button size="sm" variant="ghost" className="text-red-400 text-xs px-0 h-6" onClick={() => handleDeletePhoto(selectedMember.user_id)}>
                      Foto entfernen
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-text-secondary">Mitgliedsnummer</p>
                  <p className="font-mono text-accent font-bold">{selectedMember.member_number || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">Status</p>
                  <Badge className={selectedMember.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                    {selectedMember.is_active ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </div>
              </div>
              {selectedMember.phone && (
                <div>
                  <p className="text-xs text-text-secondary">Telefon</p>
                  <p>{selectedMember.phone}</p>
                </div>
              )}
              {selectedMember.address && (
                <div>
                  <p className="text-xs text-text-secondary">Adresse</p>
                  <p>{selectedMember.address}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-text-secondary">Mitgliedschaft</p>
                  <p className="capitalize">{selectedMember.membership_type === 'monthly' ? 'Monatlich' : selectedMember.membership_type === 'yearly' ? 'Jährlich' : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">Registriert am</p>
                  <p>{formatDate(selectedMember.created_at)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-text-secondary">Mitglied seit</p>
                  {editingDates ? (
                    <Input type="date" className="input-dark h-8 text-sm mt-1" value={memberStart} onChange={e => setMemberStart(e.target.value)} data-testid="edit-member-start" />
                  ) : (
                    <p>{selectedMember.membership_start ? formatDate(selectedMember.membership_start) : '-'}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-text-secondary">Gültig bis</p>
                  {editingDates ? (
                    <Input type="date" className="input-dark h-8 text-sm mt-1" value={memberEnd} onChange={e => setMemberEnd(e.target.value)} data-testid="edit-member-end" />
                  ) : (
                    <p>{selectedMember.membership_end ? formatDate(selectedMember.membership_end) : '-'}</p>
                  )}
                </div>
              </div>
              {editingDates ? (
                <div className="flex gap-2">
                  <Button size="sm" className="btn-primary flex-1" onClick={handleSaveDates} disabled={savingDates}>
                    {savingDates ? 'Wird gespeichert...' : 'Datum speichern'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingDates(false)}>Abbrechen</Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="w-full border-white/10" onClick={() => {
                  setMemberStart(selectedMember.membership_start ? selectedMember.membership_start.split('T')[0] : '');
                  setMemberEnd(selectedMember.membership_end ? selectedMember.membership_end.split('T')[0] : '');
                  setEditingDates(true);
                }} data-testid="edit-dates-btn">
                  <Edit className="w-3.5 h-3.5 mr-2" />
                  Mitgliedsdatum bearbeiten
                </Button>
              )}
              {/* Password Reset Button */}
              <Button
                variant="outline"
                className="w-full border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                onClick={() => { setResetPwUserId(selectedMember.user_id); setResetPwValue(''); setResetPwDialogOpen(true); }}
                data-testid="reset-password-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Passwort zurücksetzen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={resetPwDialogOpen} onOpenChange={setResetPwDialogOpen}>
        <DialogContent className="bg-surface border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Passwort zurücksetzen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Neues Passwort *</Label>
              <Input
                type="text"
                className="input-dark"
                placeholder="Neues Passwort (min. 6 Zeichen)"
                value={resetPwValue}
                onChange={(e) => setResetPwValue(e.target.value)}
                data-testid="reset-password-input"
              />
            </div>
            <Button
              className="btn-primary w-full"
              onClick={handleResetPassword}
              disabled={resettingPw || resetPwValue.length < 6}
              data-testid="reset-password-submit"
            >
              {resettingPw ? 'Wird gespeichert...' : 'Passwort setzen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Member Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { if (!open) resetCreateForm(); setCreateDialogOpen(open); }}>
        <DialogContent className="bg-surface border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              <UserPlus className="w-5 h-5 inline mr-2 text-primary" />
              Neues Mitglied anlegen
            </DialogTitle>
            <DialogDescription className="text-text-secondary">
              Erstelle ein neues Mitglied und lege den Zahlungsstatus fest.
            </DialogDescription>
          </DialogHeader>

          {/* Success Result */}
          {createdResult ? (
            <div className="space-y-4 mt-2">
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm text-center">
                <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="font-heading font-bold uppercase text-green-400">Mitglied erstellt!</p>
              </div>

              <div className="card-elevated p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-text-secondary">Mitgliedsnummer</p>
                    <p className="font-mono text-accent font-bold text-lg">{createdResult.member_number}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdResult.member_number)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">Name</p>
                  <p className="font-medium">{createdResult.name}</p>
                </div>
                <div>
                  <p className="text-xs text-text-secondary">E-Mail</p>
                  <p>{createdResult.email}</p>
                </div>
                {createdResult.generated_password && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                    <p className="text-xs text-yellow-400 mb-1">Generiertes Passwort (jetzt kopieren!):</p>
                    <div className="flex items-center justify-between">
                      <code className="text-sm font-mono">{createdResult.generated_password}</code>
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(createdResult.generated_password)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs text-text-secondary">Status</p>
                  <Badge className={createdResult.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                    {createdResult.is_active ? 'Aktiv (Bezahlt)' : 'Inaktiv'}
                  </Badge>
                </div>
              </div>

              <Button onClick={() => { resetCreateForm(); }} className="btn-primary w-full" data-testid="create-another-btn">
                Weiteres Mitglied anlegen
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreateMember} className="space-y-4 mt-2" data-testid="create-member-form">
              {/* Name & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cm-name">Name *</Label>
                  <Input id="cm-name" className="input-dark" placeholder="Vor- und Nachname"
                    value={createForm.name}
                    onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                    required data-testid="cm-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cm-email">E-Mail *</Label>
                  <Input id="cm-email" type="email" className="input-dark" placeholder="email@beispiel.de"
                    value={createForm.email}
                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                    required data-testid="cm-email-input" />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="cm-password">Passwort <span className="text-text-secondary text-xs">(leer = automatisch generiert)</span></Label>
                <Input id="cm-password" type="text" className="input-dark" placeholder="Leer lassen für automatisches Passwort"
                  value={createForm.password}
                  onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                  data-testid="cm-password-input" />
              </div>

              {/* Phone & Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cm-phone">Telefon</Label>
                  <Input id="cm-phone" className="input-dark"
                    value={createForm.phone}
                    onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cm-birth">Geburtsdatum</Label>
                  <Input id="cm-birth" type="date" className="input-dark"
                    value={createForm.birth_date}
                    onChange={e => setCreateForm({ ...createForm, birth_date: e.target.value })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cm-address">Adresse</Label>
                <Input id="cm-address" className="input-dark"
                  value={createForm.address}
                  onChange={e => setCreateForm({ ...createForm, address: e.target.value })} />
              </div>

              {/* Membership - always yearly */}
              <div className="p-3 card-surface rounded-sm">
                <p className="text-sm text-text-secondary">Mitgliedschaft</p>
                <p className="font-bold">Jährlich — 60 €</p>
              </div>

              {/* Payment Status */}
              <div className="p-4 card-elevated rounded-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-bold">Zahlung bestätigt</Label>
                    <p className="text-xs text-text-secondary">
                      Mitgliedsbeitrag wurde bereits bezahlt (z.B. bar oder Überweisung)
                    </p>
                  </div>
                  <Switch
                    checked={createForm.payment_confirmed}
                    onCheckedChange={v => setCreateForm({ ...createForm, payment_confirmed: v, is_active: v })}
                    data-testid="cm-payment-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-bold">Mitglied aktiv</Label>
                    <p className="text-xs text-text-secondary">
                      Mitglied sofort freischalten
                    </p>
                  </div>
                  <Switch
                    checked={createForm.is_active}
                    onCheckedChange={v => setCreateForm({ ...createForm, is_active: v })}
                    data-testid="cm-active-switch"
                  />
                </div>
              </div>

              <Button type="submit" disabled={creating} className="btn-primary w-full text-lg py-5" data-testid="cm-submit-btn">
                <UserPlus className="w-5 h-5 mr-2" />
                {creating ? 'Wird erstellt...' : 'Mitglied erstellen'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMembers;
